import { captureError } from '@app/lib/Sentry';
import html2canvas from 'html2canvas-pro';
import { useState } from 'react';

type UseCaptureScreenOptions = {
  includeIds?: string[];
  ignoreIds?: string[];
  ignoreClasses?: string[];
};

const useCaptureScreen = () => {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);

  const resetCapture = () => {
    setScreenshot(null);
    setFilename(null);
  };

  const captureScreen = async (options?: UseCaptureScreenOptions) => {
    try {
      const { includeIds, ignoreIds, ignoreClasses } = options || {};
      let targetElement: HTMLElement | null = document.body;

      // If includeIds are provided, create a wrapper around them
      if (includeIds && includeIds.length > 0) {
        const wrapper = document.createElement('div');
        wrapper.style.position = 'absolute';
        wrapper.style.top = '0';
        wrapper.style.left = '0';
        wrapper.style.width = '100vw';
        wrapper.style.height = '100vh';
        wrapper.style.background = 'white';
        wrapper.style.zIndex = '999999'; // Ensure it appears on top

        includeIds.forEach((id) => {
          const element = document.getElementById(id);
          if (element) {
            const clonedElement = element.cloneNode(true) as HTMLElement;
            wrapper.appendChild(clonedElement);
          }
        });

        document.body.appendChild(wrapper);
        targetElement = wrapper;
      }

      // Hide ignored elements before capturing
      const hiddenElements: HTMLElement[] = [];
      if (ignoreIds && ignoreIds.length > 0) {
        ignoreIds.forEach((id) => {
          const element = document.getElementById(id);
          if (element) {
            hiddenElements.push(element);
            element.style.visibility = 'hidden';
          }
        });
      }
      if (ignoreClasses && ignoreClasses.length > 0) {
        ignoreClasses.forEach((className) => {
          const elements = document.getElementsByClassName(className);

          if (elements.length > 0) {
            Array.from(elements).forEach((element) => {
              hiddenElements.push(element as HTMLElement);

              (element as HTMLElement).style.visibility = 'hidden';
            });
          }
        });
      }

      const canvas = await html2canvas(targetElement, { useCORS: true });
      const base64Image = canvas.toDataURL('image/png').split(',')[1];

      setScreenshot(base64Image);
      setFilename(`screenshot-${Date.now()}.png`);

      // Restore ignored elements
      hiddenElements.forEach((element) => (element.style.visibility = 'visible'));

      // Remove wrapper if used
      if (targetElement !== document.body) {
        document.body.removeChild(targetElement);
      }

      return { base64: base64Image, filename: `screenshot-${Date.now()}.png` };
    } catch (error) {
      captureError(error as Error, {
        context: 'Capture screen - take screenshot',
      });
      return null;
    }
  };

  const downloadScreen = () => {
    if (!screenshot || !filename) return;

    const link = document.createElement('a');
    link.href = `data:image/png;base64,${screenshot}`;
    link.download = filename;
    link.click();
  };

  return { screenshot, filename, captureScreen, downloadScreen, resetCapture };
};

export default useCaptureScreen;
